import { useEffect, useRef, useState } from 'react';
import { Lock, Trash2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { parseSelectableDate } from '@/components/utils/dateUtils';
import { useI18n } from '@/hooks/useI18n';
import { IInstallmentDraft } from '@/lib/builder/models/payment-plan.model';
import { amountFromPercent, percentOf } from '@/lib/builder/utils/payment-plan.utils';
import { ReminderOffsetsField } from './ReminderOffsetsField';

/** Unita' in cui il TD sta scrivendo la rata. Solo UI: non entra nella bozza salvata. */
type AmountUnit = '€' | '%';

interface InstallmentEditorRowProps {
    installment: IInstallmentDraft;
    index: number;
    /** Rata gia' incassata: importo e scadenza diventano sola lettura. */
    isPaid: boolean;
    /**
     * Estremi selezionabili nel calendario (ISO), calcolati dalle rate vicine: le scadenze
     * devono crescere riga dopo riga, quindi i giorni gia' occupati dalle altre rate — e
     * tutti quelli oltre — restano disabilitati invece di produrre un errore al salvataggio.
     */
    minDueDate?: string;
    maxDueDate?: string;
    errors?: Set<string>;
    itineraryTotal: number;
    formatAmount: (value: number) => string;
    canRemove: boolean;
    /**
     * Rata da cui si e' arrivati qui (dalla lista scadenze della dashboard). L'editor apre
     * comunque il piano intero — gli importi devono quadrare col totale — ma questa riga
     * viene portata a schermo e messa a fuoco, altrimenti con otto rate si finisce a
     * cercare a occhio quale si era cliccata.
     */
    isHighlighted?: boolean;
    onChange: (index: number, patch: Partial<IInstallmentDraft>) => void;
    onRemove: (index: number) => void;
}

export const InstallmentEditorRow = ({
    installment,
    index,
    isPaid,
    minDueDate,
    maxDueDate,
    errors,
    itineraryTotal,
    formatAmount,
    canRemove,
    isHighlighted = false,
    onChange,
    onRemove,
}: InstallmentEditorRowProps) => {
    const { t } = useI18n();
    const hasError = (field: string) => errors?.has(field) ?? false;
    const rowRef = useRef<HTMLTableRowElement>(null);
    const labelRef = useRef<HTMLInputElement>(null);

    // Unita' di scrittura della rata, per riga: chi ragiona per acconti pensa "30%", chi
    // ragiona per saldi pensa in euro, e la stessa tabella puo' contenere entrambi.
    const [unit, setUnit] = useState<AmountUnit>('€');
    /**
     * Testo in corso di digitazione. Finche' e' valorizzato comanda lui, altrimenti il campo
     * si rilegge dall'importo: senza questo, "0," o "12." verrebbero normalizzati a meta'
     * battuta e sarebbe impossibile scrivere i decimali.
     */
    const [rawInput, setRawInput] = useState<string | null>(null);

    const percent = percentOf(installment.amount, itineraryTotal);
    const displayedAmount =
        rawInput ?? (unit === '%' ? String(percent) : String(installment.amount));

    // L'importo si aggiorna a ogni battuta: e' quello che rende il campo "vivo", e in
    // percentuale l'unico modo di vedere subito quanto fa la rata.
    const handleAmountInput = (value: string) => {
        setRawInput(value);
        const parsed = parseFloat(value.replace(',', '.'));
        const safe = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
        onChange(index, {
            amount: unit === '%' ? amountFromPercent(safe, itineraryTotal) : safe,
        });
    };

    // Cambiando unita' non si tocca l'importo: cambia solo come lo si sta guardando.
    const switchUnit = (next: AmountUnit) => {
        if (next === unit) return;
        setRawInput(null);
        setUnit(next);
    };

    // Una sola volta, all'apertura: se rieseguito a ogni battuta rimetterebbe la riga a
    // fuoco mentre si sta scrivendo in un altro campo.
    useEffect(() => {
        if (!isHighlighted) return;
        rowRef.current?.scrollIntoView({ block: 'nearest' });
        labelRef.current?.focus();
        labelRef.current?.select();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <tr
        ref={rowRef}
        className={`border-b border-gray-100 last:border-b-0 ${
          isHighlighted ? 'bg-amber-50/70 outline outline-1 outline-amber-300' : ''
        }`}
      >
        <td className="py-1.5 pl-3 pr-1.5 text-xs text-gray-400 tabular-nums align-middle">
          {index + 1}
        </td>

        <td className="py-1.5 pr-1.5 align-middle">
          <Input
            ref={labelRef}
            value={installment.label}
            onChange={(e) => onChange(index, { label: e.target.value })}
            className={`h-8 text-sm ${hasError("label") ? "border-red-400" : ""}`}
            aria-label={t("checkout.paymentPlan.columns.label")}
          />
        </td>

        <td className="py-1.5 pr-1.5 align-middle">
          {/* Si scrive in euro o in percentuale, ma quello che si salva e' sempre
              l'importo: la percentuale la ricalcola comunque il database. */}
          <div className="flex flex-col items-end gap-0.5">
            <div className="flex w-full items-center gap-1">
              <Input
                type="text"
                inputMode="decimal"
                value={displayedAmount}
                disabled={isPaid}
                onChange={(e) => handleAmountInput(e.target.value)}
                onBlur={() => setRawInput(null)}
                className={`h-8 flex-1 min-w-0 text-sm text-right tabular-nums ${hasError("amount") ? "border-red-400" : ""}`}
                aria-label={t("checkout.paymentPlan.columns.amount")}
              />
              <div className="flex rounded-full border border-gray-200 overflow-hidden flex-shrink-0">
                {(["€", "%"] as AmountUnit[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    /* Senza un totale la percentuale non ha un riferimento: il campo
                       resterebbe fermo a zero qualunque cosa si scriva. */
                    disabled={isPaid || (value === "%" && itineraryTotal <= 0)}
                    onClick={() => switchUnit(value)}
                    className={`px-1.5 py-0.5 text-[11px] leading-4 transition-colors disabled:opacity-50 ${
                      value === "%" ? "border-l border-gray-200" : ""
                    } ${
                      unit === value
                        ? "bg-gray-100 font-semibold text-black"
                        : "text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
            </div>
            {/* Sotto il campo c'e' sempre l'altra unita': serve a controllare a colpo
                d'occhio che una rata scritta al 30% sia la cifra che ci si aspetta. */}
            <span className="text-[11px] text-gray-400 tabular-nums whitespace-nowrap">
              {unit === "%"
                ? formatAmount(installment.amount)
                : t("checkout.paymentPlan.percentOfTotal", {
                    percent: percent.toFixed(2),
                  })}
            </span>
          </div>
        </td>

        <td className="py-1.5 pr-1.5 align-middle w-[142px]">
          <DatePicker
            value={installment.due_date}
            onSelect={(date) => onChange(index, { due_date: date })}
            placeholder={t("checkout.paymentPlan.columns.dueDate")}
            hasError={hasError("due_date")}
            isDisabled={isPaid}
            minDate={parseSelectableDate(minDueDate)}
            maxDate={parseSelectableDate(maxDueDate)}
          />
        </td>

        <td className="py-1.5 pr-1.5 align-middle text-center">
          <Switch
            checked={installment.notify_on_due}
            onCheckedChange={(checked) =>
              onChange(index, { notify_on_due: checked })
            }
            aria-label={t("checkout.paymentPlan.columns.notifyOnDue")}
          />
        </td>

        {/* Unica colonna senza larghezza fissa: i chip crescono di numero, gli altri
            campi no, quindi lo spazio in piu' della tabella finisce qui. */}
        <td className="py-1.5 pr-1.5 align-middle">
          <ReminderOffsetsField
            value={installment.reminder_offsets}
            onChange={(offsets) =>
              onChange(index, { reminder_offsets: offsets })
            }
          />
        </td>

        <td className="py-1.5 align-middle text-center">
          {isPaid ? (
            /* La rata incassata non si cancella: al posto del cestino resta il segno che
               la riga e' bloccata. L'importo lo dice gia' la colonna accanto. */
            <span
              className="inline-flex text-green-700"
              title={`${t("checkout.paymentPlan.status.paid")} · ${formatAmount(installment.amount)}`}
              aria-label={t("checkout.paymentPlan.status.paid")}
            >
              <Lock className="w-3.5 h-3.5" />
            </span>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-gray-400 hover:text-red-600"
              disabled={!canRemove}
              onClick={() => onRemove(index)}
              aria-label={t("checkout.paymentPlan.actions.removeInstallment")}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </td>
      </tr>
    );
};
